<template>
    <b-modal content-class="argus-glass-dialog"
        v-model="show"
        title="Изменение статуса заявки"
        @ok="handleOk"
        @cancel="handleCancel"
        :ok-disabled="!isValid || loading"
        :cancel-disabled="loading"
        ok-title="Изменить статус"
        ok-variant="warning"
        cancel-title="Отмена"
        cancel-variant="outline-secondary"
        size="lg"
        scrollable
    >
        <div>
            <b-alert variant="warning" show class="mb-3">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                <strong>Внимание!</strong> Вы изменяете статус заявки напрямую. Это действие будет залогировано с пометкой "Admin override".
            </b-alert>

            <b-form-group label="Текущий статус:" label-for="current-status">
                <b-badge :variant="currentStatusVariant" class="p-2">
                    {{ currentStatusLabel }}
                </b-badge>
            </b-form-group>

            <b-form-group 
                label="Новый статус:" 
                label-for="new-status" 
                :invalid-feedback="'Выберите статус'" 
                :state="selectedStatus ? null : false"
            >
                <b-form-select
                    id="new-status"
                    v-model="selectedStatus"
                    :options="statusOptions"
                    :disabled="loading"
                    :state="selectedStatus ? null : false"
                >
                    <template #first>
                        <b-form-select-option :value="null" disabled>Выберите статус...</b-form-select-option>
                    </template>
                </b-form-select>
            </b-form-group>

            <div v-if="allowCloseWithoutMechanicActions && selectedStatus === 'completed'" class="mb-3">
                <b-form-checkbox v-model="closeWithoutMechanicActions" :disabled="loading">
                    Закрыть старую заявку без изменения распределения техники
                </b-form-checkbox>
                <small class="form-text text-muted">
                    Техника останется у текущих получателей и на текущих объектах. Выдача, перемещения и другие действия справочника техники выполняться не будут. Причина сохранится в истории заявки.
                </small>
            </div>

            <b-form-group
                :label="commentRequired ? 'Комментарий *:' : 'Комментарий (необязательно):'"
                label-for="status-comment"
                invalid-feedback="Введите комментарий при закрытии заявки"
                :state="commentState"
            >
                <DiscussionEditor
                    id="status-comment"
                    v-model="comment"
                    placeholder="Укажите причину изменения статуса..."
                    :disabled="loading"
                    :state="commentState"
                />
                <small class="form-text text-muted">
                    Комментарий будет сохранен в истории заявки
                </small>
            </b-form-group>
        </div>
        
        <template #modal-footer="{ ok, cancel }">
            <b-button variant="outline-secondary" @click="cancel" :disabled="loading">
                Отмена
            </b-button>
            <b-button 
                variant="warning" 
                @click="ok" 
                :disabled="!isValid || loading"
            >
                <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                {{ loading ? 'Изменение...' : 'Изменить статус' }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { getAllStatuses } from '@/utils/requestStatuses';
import { isFinalRequestStatus } from '@/utils/requestClosingComment';

export default {
    name: 'ChangeStatusModal',
    props: {
        allowCloseWithoutMechanicActions: {type: Boolean, default: false},
        value: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        currentStatus: {
            type: String,
            default: ''
        },
        currentStatusLabel: {
            type: String,
            default: ''
        },
        currentStatusVariant: {
            type: String,
            default: 'secondary'
        },
        closingCommentRequired: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            closeWithoutMechanicActions: false,
            selectedStatus: null,
            comment: ''
        };
    },
    computed: {
        show: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        statusOptions() {
            const allStatuses = getAllStatuses();
            return allStatuses.map(status => ({
                value: status.key,
                text: `${status.label} (${status.key})`,
                description: status.description
            }));
        },
        commentRequired() {
            return this.closeWithoutMechanicActions || (this.closingCommentRequired && isFinalRequestStatus(this.selectedStatus));
        },
        trimmedComment() {
            return String(this.comment || '').trim();
        },
        commentState() {
            if (!this.commentRequired) return null;
            return this.trimmedComment.length > 0 ? true : false;
        },
        isValid() {
            return Boolean(this.selectedStatus) && (!this.commentRequired || this.trimmedComment.length > 0);
        }
    },
    watch: {
        selectedStatus() {
            this.closeWithoutMechanicActions = false;
        },
        value(newVal) {
            if (!newVal) {
                this.closeWithoutMechanicActions = false;
                this.selectedStatus = null;
                this.comment = '';
            }
        }
    },
    methods: {
        handleOk() {
            if (!this.isValid) {
                return;
            }
            this.$emit('confirm', {
                closeWithoutMechanicActions: this.allowCloseWithoutMechanicActions && this.selectedStatus === 'completed' && this.closeWithoutMechanicActions,
                targetStatus: this.selectedStatus,
                comment: this.trimmedComment || null
            });
        },
        handleCancel() {
            this.closeWithoutMechanicActions = false;
            this.$emit('input', false);
            this.selectedStatus = null;
            this.comment = '';
        }
    }
};
</script>
